import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Badge } from '../../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { RefreshCw, Eye, Search } from 'lucide-react';
import API_CONSTRUCTOR from '../../../models/api-model';
import { formatCurrency, formatDate } from '../../../utils/adminUtils';
import { INVESTMENT_STATUS, INVESTMENT_STATUS_COLORS, INVESTMENT_STATUS_LABELS } from '../../../constants/investmentStatus';
import { toast } from 'sonner';

export const AdminInvestments = () => {
  const navigate = useNavigate();
  
  const [commitments, setCommitments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const loadCommitments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const api = API_CONSTRUCTOR();
      
      // Build query params
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (searchQuery) params.append('search', searchQuery);
      
      const url = `/admin/commitments${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await api.get(url);
      
      setCommitments(response.data || []);
    } catch (err) {
      console.error('Error loading commitments:', err);
      setError(err.response?.data?.error || 'Failed to load commitments');
      toast.error('Failed to load commitments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCommitments();
  }, [statusFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    loadCommitments();
  };

  const getStatusColor = (status) => {
    return INVESTMENT_STATUS_COLORS[status] || 'bg-gray-100 text-gray-800';
  };

  const formatStatus = (status) => {
    return INVESTMENT_STATUS_LABELS[status] || status;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Investment Management</h1>
          <p className="text-gray-600 mt-1">Review and manage investor commitments</p>
        </div>
        <Button onClick={loadCommitments} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="flex gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search by investor name, property address..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                icon={<Search className="w-4 h-4" />}
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending Review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="funded">Funded</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="withdrawn">Withdrawn</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit">Search</Button>
          </form>
        </CardContent>
      </Card>

      {/* Commitments Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            All Commitments
            {commitments.length > 0 && (
              <span className="ml-2 text-sm font-normal text-gray-500">
                ({commitments.length} {commitments.length === 1 ? 'commitment' : 'commitments'})
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading commitments...</div>
          ) : error ? (
            <div className="text-center py-8 text-red-500">{error}</div>
          ) : commitments.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No commitments found</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Investor</TableHead>
                    <TableHead>Property</TableHead>
                    <TableHead>Commitment</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Expected Profit</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {commitments.map((commitment) => (
                    <TableRow key={commitment.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{commitment.investor_name}</div>
                          <div className="text-sm text-gray-500">{commitment.investor_email}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-xs">
                          <div className="font-medium truncate">{commitment.property_address}</div>
                          <div className="text-sm text-gray-500">
                            {commitment.property_city}, {commitment.property_state}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-semibold text-orange-600">
                          {commitment.commitment_percentage}%
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{formatCurrency(commitment.commitment_amount)}</div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-green-600">
                          {formatCurrency(commitment.expected_profit_share)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(commitment.commitment_status)}>
                          {formatStatus(commitment.commitment_status)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{formatDate(commitment.created_at)}</div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/admin/investments/${commitment.id}`)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminInvestments;
